// ============================================================
// engine — resolved design model → concrete CSS
// ============================================================
//
// Pure functions only; no React. The size tables are calibrated so the "md"
// preset reproduces the legacy renderer's exact clamp() values — legacy rows
// normalize to "md" everywhere, so they paint the same text sizes as before.

import React from "react";
import {
  ResolvedStepDesign,
  DesignBackground,
  ShadowPreset,
  PaddingPreset,
  TextSizePreset,
  EntryAnimation,
  FONT_FALLBACK_STACK,
  POPUP_FONTS,
  CustomFont,
} from "@/shared/types/popup-design";

// ---------- text size tables ("md" === legacy clamps) ----------

const HEADING_SIZES: Record<TextSizePreset, string> = {
  sm: "clamp(18px, 4vw, 24px)",
  md: "clamp(21px, 4.75vw, 30px)", // legacy
  lg: "clamp(24px, 5.5vw, 38px)",
  xl: "clamp(28px, 6.5vw, 48px)",
};

const SUBHEADING_SIZES: Record<TextSizePreset, string> = {
  sm: "clamp(12px, 3vw, 15px)",
  md: "clamp(14px, 3.6vw, 18px)", // legacy
  lg: "clamp(16px, 4vw, 21px)",
  xl: "clamp(18px, 4.4vw, 24px)",
};

const BODY_SIZES: Record<TextSizePreset, string> = {
  sm: "clamp(12px, 2.8vw, 14px)",
  md: "clamp(14px, 3.2vw, 16px)", // legacy welcome body
  lg: "clamp(16px, 3.6vw, 18px)",
  xl: "clamp(18px, 4vw, 21px)",
};

/** The success-step message historically used a larger clamp than body copy. */
const MESSAGE_SIZES: Record<TextSizePreset, string> = {
  sm: "clamp(13px, 3.2vw, 16px)",
  md: "clamp(15px, 3.8vw, 20px)", // legacy success message
  lg: "clamp(17px, 4.2vw, 24px)",
  xl: "clamp(19px, 4.6vw, 28px)",
};

const FOOTER_SIZES: Record<TextSizePreset, string> = {
  sm: "clamp(11px, 2.6vw, 13px)",
  md: "clamp(12px, 3vw, 15px)", // legacy
  lg: "clamp(13px, 3.2vw, 17px)",
  xl: "clamp(14px, 3.4vw, 19px)",
};

export const FORM_MAX_WIDTH = "min(100%, 360px)"; // legacy form column cap

// ---------- shadows ----------

const SHADOWS: Record<ShadowPreset, string> = {
  none: "none",
  sm: "0 4px 12px rgba(0,0,0,0.08)",
  md: "0 12px 32px rgba(0,0,0,0.14)",
  lg: "0 25px 50px -12px rgba(0,0,0,0.25)", // ≈ tailwind shadow-2xl (legacy)
  glow: "0 0 40px rgba(0,0,0,0.18), 0 12px 32px rgba(0,0,0,0.16)",
};

export const shadowCss = (preset: ShadowPreset): string => SHADOWS[preset];

// ---------- "lit" button finish (resources.flowapt.com sign-in, 15 Oct 2026) ----------

type Rgb = [number, number, number];

/** #rgb / #rrggbb / rgb(a)() → [r, g, b]; anything else (named, var(), gradients) → null. */
export function parseRgb(color: string | undefined): Rgb | null {
  const c = (color || "").trim();
  const hex = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].replace(/./g, (x) => x + x) : hex[1];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = c.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
}

const towardWhite = ([r, g, b]: Rgb, t: number): Rgb => [r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t];
const rgbCss = ([r, g, b]: Rgb, a = 1) =>
  a === 1 ? `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})` : `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;

/** Inline style for a lit button, or null when the colour cannot be parsed
 *  (the button then renders flat). The sign-in button's recipe, derived from
 *  any colour: gradient from 13% toward white down to the colour (#171917 →
 *  #353735; the hand-tuned original was #363b37), shadows inset 0 1px 0 white
 *  20%, inset 0 -2px 0 black 40%, 0 1px 2px black 40%, 0 8px 24px -6px the
 *  colour at 60%. Hover and pressed gradients ride CSS vars read by the
 *  .fp-btn-lit rules in RENDERER_CSS. */
export function litButtonStyle(background: string, hoverBackground?: string): React.CSSProperties | null {
  const base = parseRgb(background);
  if (!base) return null;
  const hover = parseRgb(hoverBackground) ?? towardWhite(base, 0.045);
  return {
    backgroundImage: `linear-gradient(180deg, ${rgbCss(towardWhite(base, 0.13))}, ${rgbCss(base)})`,
    boxShadow: `inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -2px 0 rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px -6px ${rgbCss(base, 0.6)}`,
    ["--fp-btn-lit-hover" as string]: `linear-gradient(180deg, ${rgbCss(towardWhite(hover, 0.13))}, ${rgbCss(hover)})`,
    ["--fp-btn-lit-active" as string]: `linear-gradient(180deg, ${rgbCss(base)}, ${rgbCss(towardWhite(base, 0.07))})`,
  };
}

// ---------- padding ----------

export interface PaddingScale {
  /** content column padding for panels without an image */
  soloX: string;
  soloY: string;
  /** content column padding when an image shares the popup */
  withImage: string;
}

const PADDINGS: Record<PaddingPreset, PaddingScale> = {
  compact: { soloX: "16px", soloY: "20px", withImage: "16px" },
  comfortable: { soloX: "24px", soloY: "32px", withImage: "24px" }, // legacy px-6 py-8 / p-6
  spacious: { soloX: "40px", soloY: "48px", withImage: "32px" },
};

export const paddingScale = (preset: PaddingPreset): PaddingScale => PADDINGS[preset];

// ---------- background ----------

/** The layered `background-image` value for an image backdrop: the legibility
 *  overlay (when set) stacked over the picture. Shared by the desktop inline
 *  style and the phone-only `--fp-bg-m` override so both carry the overlay. */
export function backgroundImageValue(
  image: { url: string; overlayColor: string; overlayOpacity: number },
  url: string = image.url
): string {
  const overlay = image.overlayColor
    ? `linear-gradient(${withOpacity(image.overlayColor, image.overlayOpacity)}, ${withOpacity(image.overlayColor, image.overlayOpacity)}), `
    : "";
  return `${overlay}url("${url}")`;
}

/** The phone backdrop as layered CSS backgrounds: overlay, optional top and
 *  bottom edge strips (full width, pinned to the visible edges), then the
 *  base picture cover-fitted. Returns the four longhand values the
 *  `.fp-bg-m` rule reads; null when there is nothing phone-specific. */
export function mobileBackdropLayers(image: {
  url: string;
  mobileUrl?: string;
  mobileTopUrl?: string;
  mobileBottomUrl?: string;
  overlayColor: string;
  overlayOpacity: number;
}): { image: string; size: string; position: string; repeat: string } | null {
  const base = image.mobileUrl || image.url;
  if (!image.mobileUrl && !image.mobileTopUrl && !image.mobileBottomUrl) return null;
  if (!base) return null;
  const layers: { img: string; size: string; pos: string }[] = [];
  if (image.overlayColor) {
    const o = withOpacity(image.overlayColor, image.overlayOpacity);
    layers.push({ img: `linear-gradient(${o}, ${o})`, size: "auto", pos: "center" });
  }
  if (image.mobileTopUrl) layers.push({ img: `url("${image.mobileTopUrl}")`, size: "100% auto", pos: "top center" });
  if (image.mobileBottomUrl) layers.push({ img: `url("${image.mobileBottomUrl}")`, size: "100% auto", pos: "bottom center" });
  layers.push({ img: `url("${base}")`, size: "cover", pos: "center" });
  return {
    image: layers.map((l) => l.img).join(", "),
    size: layers.map((l) => l.size).join(", "),
    position: layers.map((l) => l.pos).join(", "),
    repeat: layers.map(() => "no-repeat").join(", "),
  };
}

export function backgroundCss(bg: DesignBackground): React.CSSProperties {
  switch (bg.type) {
    case "gradient":
      if (bg.gradient) {
        return {
          backgroundImage: `linear-gradient(${bg.gradient.angle}deg, ${bg.gradient.from}, ${bg.gradient.to})`,
          backgroundColor: bg.color,
        };
      }
      return { backgroundColor: bg.color };
    case "image":
      if (bg.image?.url) {
        return {
          backgroundImage: backgroundImageValue(bg.image),
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: bg.color,
        };
      }
      return { backgroundColor: bg.color };
    case "solid":
    default:
      return { backgroundColor: bg.color };
  }
}

/** Parses #rgb/#rrggbb/rgb()/rgba() into channels; null for anything else. */
function parseColorChannels(
  color: string
): { r: number; g: number; b: number } | null {
  const trimmed = color.trim();
  const hexMatch = trimmed.match(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  const rgbMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbMatch) {
    const parts = rgbMatch[1].split(",").map((p) => parseFloat(p));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => !Number.isNaN(n))) {
      return { r: parts[0], g: parts[1], b: parts[2] };
    }
  }
  return null;
}

/** Perceived-luminance lightness test. Unparseable colors count as light —
 *  that keeps legacy popups (all white) on the exact legacy code paths. */
export function isLightColor(color: string): boolean {
  const c = parseColorChannels(color);
  if (!c) return true;
  return (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255 > 0.72;
}

/** Is the resolved panel background light? Drives the adaptive error/success
 *  treatments: light panels keep the exact legacy grays/reds; dark or image
 *  panels switch to translucent-white surfaces and lighter error text. */
export function isLightBackground(bg: DesignBackground): boolean {
  switch (bg.type) {
    case "image":
      return bg.image?.url ? false : isLightColor(bg.color);
    case "gradient":
      return bg.gradient
        ? isLightColor(bg.gradient.from) && isLightColor(bg.gradient.to)
        : isLightColor(bg.color);
    case "solid":
    default:
      return isLightColor(bg.color);
  }
}

/** Applies an opacity to a hex or rgb() color; passes rgba/others through. */
export function withOpacity(color: string, opacity: number): string {
  const clamped = Math.max(0, Math.min(1, opacity));
  const hexMatch = color.trim().match(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${clamped})`;
  }
  const rgbMatch = color.trim().match(/^rgb\(([^)]+)\)$/i);
  if (rgbMatch) return `rgba(${rgbMatch[1]},${clamped})`;
  return color;
}

// ---------- sizing (ported verbatim from the legacy renderer) ----------

const FALLBACK_CLAMP = "clamp(280px, 92vw, 800px)";
const DEFAULT_MIN_WIDTH = "min(88vw, 340px)";

function sanitizeDimension(value?: string): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === "0") return undefined;
  const lowered = trimmed.toLowerCase();
  if (["auto", "initial", "inherit", "unset"].includes(lowered)) return undefined;
  return trimmed;
}

export interface ResolvedWidths {
  width: string;
  minWidth: string;
  maxWidth: string;
}

/** Modal width resolution — identical math to the legacy popupStyles memo. */
export function resolveModalWidths(size: ResolvedStepDesign["size"]): ResolvedWidths {
  const presetWidths: Record<string, ResolvedWidths> = {
    sm: { width: "min(92vw, 440px)", minWidth: "min(88vw, 320px)", maxWidth: "min(92vw, 440px)" },
    md: { width: "min(92vw, 640px)", minWidth: "min(88vw, 340px)", maxWidth: "min(92vw, 640px)" },
    lg: { width: FALLBACK_CLAMP, minWidth: DEFAULT_MIN_WIDTH, maxWidth: FALLBACK_CLAMP },
  };
  if (size.preset !== "custom") {
    return presetWidths[size.preset] ?? presetWidths.lg;
  }

  const rawWidth = sanitizeDimension(size.width);
  const rawMaxWidth = sanitizeDimension(size.maxWidth);
  const rawMinWidth = sanitizeDimension(size.minWidth);

  const maxWidth = rawMaxWidth ? `clamp(0px, 92vw, ${rawMaxWidth})` : FALLBACK_CLAMP;
  // The builder exposes ONE width control for a custom popup (the "Max width"
  // input and the panel edge-drag) and both write `maxWidth`. A `width` that
  // disagrees with it is a leftover — legacy `dimensions.width`, or a row
  // written by hand — so an explicit maxWidth outranks it. Before this,
  // `min(width, maxWidth)` pinned the popup at the stale `width` and every
  // width edit saved fine but rendered nothing (KOIKOI, 4 Sep 2026: saved
  // 873px, rendered 440px). A design with no `width` keeps the fallback clamp.
  const width = !rawWidth
    ? FALLBACK_CLAMP
    : rawMaxWidth
      ? maxWidth
      : `min(${rawWidth}, ${maxWidth})`;
  const minWidth = rawMinWidth ? `min(${rawMinWidth}, ${maxWidth})` : DEFAULT_MIN_WIDTH;

  return { width, minWidth, maxWidth };
}

// ---------- text styles ----------

type TextKind = "heading" | "subheading" | "body" | "message" | "footer";

const SIZE_TABLES: Record<TextKind, Record<TextSizePreset, string>> = {
  heading: HEADING_SIZES,
  subheading: SUBHEADING_SIZES,
  body: BODY_SIZES,
  message: MESSAGE_SIZES,
  footer: FOOTER_SIZES,
};

const LINE_HEIGHTS: Record<TextKind, number> = {
  heading: 1.08,
  subheading: 1.45,
  body: 1.5,
  message: 1.55,
  footer: 1.4,
};

/** Custom-px sizes get the same responsive treatment as the presets: exact on
 *  desktop, scaling down on small screens. Coefficients generalize the preset
 *  tables (xl heading = clamp(28px, 6.5vw, 48px) → min ≈ 0.58×, vw ≈ px/7.4). */
function customSizeCss(px: number): string {
  if (px < 14) return `${px}px`;
  const min = Math.max(12, Math.round(px * 0.58));
  const vw = (px / 7.4).toFixed(2);
  return `clamp(${min}px, ${vw}vw, ${px}px)`;
}

/** Phone-size hook for a text element: the class + CSS var that the
 *  `.fp-fs-m` rule reads under 768px. Empty when no phone size is set. */
export function mobileSizeProps(
  style: { mobileSizePx?: number } | undefined
): { className?: string; style?: React.CSSProperties } {
  const px = style?.mobileSizePx;
  if (!px || px <= 0) return {};
  return { className: "fp-fs-m", style: { ["--fp-fs-m" as string]: `${px}px` } };
}

const spacePx = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.min(Math.round(v), 200) : null;

/** Designer-set space above an element (`SpaceAbove`: spaceAbovePx /
 *  mobileSpaceAbovePx). Returns the class + CSS var pair the `.fp-mt` /
 *  `.fp-mt-m` rules in RENDERER_CSS read with !important: the widget shell's
 *  `.space-y-N > :not([hidden]) ~ :not([hidden])` margins are !important too
 *  and beat an inline margin. Empty when neither is set (the layout's own
 *  gaps stay exactly as they were). */
export function spaceAboveProps(
  style: { spaceAbovePx?: number | null; mobileSpaceAbovePx?: number | null } | undefined
): { className?: string; style?: React.CSSProperties } {
  const desktop = spacePx(style?.spaceAbovePx);
  const phone = spacePx(style?.mobileSpaceAbovePx);
  if (desktop === null && phone === null) return {};
  const classes: string[] = [];
  const vars: Record<string, string> = {};
  if (desktop !== null) {
    classes.push("fp-mt");
    vars["--fp-mt"] = `${desktop}px`;
  }
  if (phone !== null) {
    classes.push("fp-mt-m");
    vars["--fp-mt-m"] = `${phone}px`;
  }
  return { className: classes.join(" "), style: vars as React.CSSProperties };
}

/** Designer-set space BETWEEN the form fields (InputsStyle gapPx /
 *  mobileGapPx): the same class + var pattern as spaceAboveProps, read by the
 *  `.fp-fgap` / `.fp-fgap-m` child rules in RENDERER_CSS. Empty when neither
 *  is set, so the layout's space-y gaps stay exactly as they were. */
export function fieldGapProps(
  style: { gapPx?: number | null; mobileGapPx?: number | null } | undefined
): { className?: string; style?: React.CSSProperties } {
  const desktop = spacePx(style?.gapPx);
  const phone = spacePx(style?.mobileGapPx);
  if (desktop === null && phone === null) return {};
  const classes: string[] = [];
  const vars: Record<string, string> = {};
  if (desktop !== null) {
    classes.push("fp-fgap");
    vars["--fp-fgap"] = `${desktop}px`;
  }
  if (phone !== null) {
    classes.push("fp-fgap-m");
    vars["--fp-fgap-m"] = `${phone}px`;
  }
  return { className: classes.join(" "), style: vars as React.CSSProperties };
}

export function textCss(
  kind: TextKind,
  style: {
    color: string;
    size: TextSizePreset;
    sizePx?: number;
    weight: number;
    align?: "left" | "center" | "right";
    italic?: boolean;
    fontFamily?: string;
    lineHeight?: number;
    letterSpacing?: string;
  }
): React.CSSProperties {
  return {
    color: style.color,
    fontSize:
      style.sizePx && style.sizePx > 0
        ? customSizeCss(style.sizePx)
        : SIZE_TABLES[kind][style.size],
    lineHeight:
      style.lineHeight && style.lineHeight > 0 ? style.lineHeight : LINE_HEIGHTS[kind],
    fontWeight: style.weight,
    textAlign: style.align ?? "center", // legacy centered all copy
    ...(style.italic ? { fontStyle: "italic" } : {}),
    ...(style.letterSpacing ? { letterSpacing: style.letterSpacing } : {}),
    // Per-element family beats the panel's theme font (set on the container)
    ...(style.fontFamily ? { fontFamily: fontStack(style.fontFamily) } : {}),
  };
}

// ---------- inline rich text (bold / italic / underline / links / colour) ----------

// P = a paragraph inside one text element (8 Oct 2026): `.fp-rich p + p`
// gives it a paragraph gap, which a <br> cannot. No live copy used <p> before
// it was allowed (0 of 89 popups), so nothing that rendered changes.
const ALLOWED_INLINE_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "A", "BR", "SPAN", "P"]);

/** A colour value we are willing to write back into a style attribute:
 *  #rgb/#rrggbb(aa), rgb()/rgba() with numeric channels only, or a bare CSS
 *  colour keyword. Nothing here can carry a url()/expression()/escape, so the
 *  rebuilt `color: <value>` is inert by construction. */
const SAFE_COLOR_VALUE = /^#[0-9a-f]{3,8}$|^rgba?\([\d\s.,%/]+\)$|^[a-z]+$/i;

/** Extracts the ONE declaration allowed to survive a style attribute: `color`.
 *  Returns null when absent or not a plain colour value. */
function safeInlineColor(style: string | null): string | null {
  if (!style) return null;
  for (const declaration of style.split(";")) {
    const split = declaration.indexOf(":");
    if (split < 0) continue;
    if (declaration.slice(0, split).trim().toLowerCase() !== "color") continue;
    const value = declaration
      .slice(split + 1)
      .replace(/!important/i, "")
      .trim();
    if (SAFE_COLOR_VALUE.test(value)) return value;
  }
  return null;
}

/**
 * Sanitizes copy that may contain limited inline HTML from the editor's
 * rich-text fields. Allowlist only: b/strong/i/em/u/a/br/span/p, no attributes
 * except a[href] restricted to http(s) and a `color:` style on inline tags.
 * Plain strings pass through untouched.
 *
 * The colour exception exists because an element carries exactly ONE colour:
 * an accent word inside a heading ("This is / *the actual product* / you are
 * reading about.") is otherwise impossible to express. Only `color` survives,
 * only with a value matching SAFE_COLOR_VALUE, and the attribute is REBUILT
 * from that parsed value rather than passed through.
 */
export function sanitizeInlineHtml(input: string | undefined | null): string {
  if (!input) return "";
  if (!/[<>]/.test(input)) return input;
  if (typeof document === "undefined") return input.replace(/<[^>]*>/g, "");

  const tmpl = document.createElement("template");
  tmpl.innerHTML = input;
  for (const el of Array.from(tmpl.content.querySelectorAll("*"))) {
    if (!ALLOWED_INLINE_TAGS.has(el.tagName)) {
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }
    const color = el.tagName === "BR" ? null : safeInlineColor(el.getAttribute("style"));
    for (const attr of Array.from(el.attributes)) {
      if (el.tagName === "A" && attr.name === "href") {
        if (!/^https?:\/\//i.test(attr.value.trim())) el.removeAttribute("href");
      } else {
        el.removeAttribute(attr.name);
      }
    }
    if (color) el.setAttribute("style", `color: ${color}`);
    if (el.tagName === "A") {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
    }
  }
  return tmpl.innerHTML;
}

// ---------- fonts ----------

const fontSlug = (family: string) => family.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/** Ensures the given fonts are loaded in the HOST document (fonts can't be
 *  declared inside shadow DOM). Three cases per family:
 *  - Inter: ships with the widget shell, nothing to do.
 *  - An org-uploaded CUSTOM font (matched in `customFonts`): a namespaced
 *    <style> of @font-face rules pointing at the public popup_fonts bucket.
 *  - A known Google font: a namespaced per-family <link> (so several fonts —
 *    theme + per-element overrides across both steps — can be live at once).
 *    Heavy cuts (800/900) load via a SEPARATE link: css2 rejects the whole
 *    request when a family lacks a requested weight, so bundling 800 into the
 *    main link would break 400–700 for families without heavy cuts. */
export function ensureFontsLoaded(
  families: Array<string | undefined | null>,
  customFonts?: CustomFont[]
): void {
  if (typeof document === "undefined") return;

  const upsertLink = (id: string, href: string) => {
    const existing = document.getElementById(id) as HTMLLinkElement | null;
    if (existing) {
      if (existing.href !== href) existing.href = href;
      return;
    }
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  };

  const upsertStyle = (id: string, css: string) => {
    const existing = document.getElementById(id) as HTMLStyleElement | null;
    if (existing) {
      if (existing.textContent !== css) existing.textContent = css;
      return;
    }
    const style = document.createElement("style");
    style.id = id;
    style.textContent = css;
    document.head.appendChild(style);
  };

  const unique = new Set<string>();
  for (const family of families) {
    if (!family || family === "Inter") continue; // Inter is always loaded by the widget shell
    unique.add(family);
  }

  for (const family of unique) {
    const slug = fontSlug(family);
    const custom = customFonts?.find((f) => f.family === family && f.faces?.length);
    if (custom) {
      const css = custom.faces
        .map((face) => {
          const url = String(face.url).replace(/["\\]/g, "");
          return `@font-face{font-family:"${family.replace(/["\\]/g, "")}";src:url("${url}");font-weight:${Number(face.weight) || 400};font-style:${face.style === "italic" ? "italic" : "normal"};font-display:swap;}`;
        })
        .join("\n");
      upsertStyle(`flowapt-popup-customfont-${slug}`, css);
      continue;
    }

    if (!POPUP_FONTS.some((f) => f.family === family)) continue;
    const encoded = encodeURIComponent(family).replace(/%20/g, "+");
    upsertLink(
      `flowapt-popup-font-${slug}`,
      `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`
    );
    upsertLink(
      `flowapt-popup-font-${slug}-heavy`,
      `https://fonts.googleapis.com/css2?family=${encoded}:wght@800&display=swap`
    );
  }
}

export function ensureFontLoaded(fontFamily: string): void {
  ensureFontsLoaded([fontFamily]);
}

export function fontStack(fontFamily: string): string {
  return `'${fontFamily}', ${FONT_FALLBACK_STACK}`;
}

// ---------- animations ----------

/** Keyframes ship in the renderer's inline <style>; this returns the
 *  animation shorthand for the popup container. "slide_down" is an internal
 *  variant used by top-positioned bars. */
export function entryAnimationCss(
  entry: EntryAnimation | "slide_down",
  durationMs: number
): React.CSSProperties {
  if (entry === "none") return {};
  const name = {
    fade: "flowapt-fade-in",
    slide_up: "flowapt-slide-up",
    slide_down: "flowapt-slide-down",
    zoom: "flowapt-zoom-in",
  }[entry];
  return {
    animation: `${name} ${durationMs}ms ease-out both`,
  };
}

export const ANIMATION_KEYFRAMES_CSS = `
@keyframes flowapt-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes flowapt-slide-up {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes flowapt-zoom-in {
  from { opacity: 0; transform: scale(0.94); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes flowapt-slide-in-right {
  from { opacity: 0; transform: translateX(32px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes flowapt-slide-in-left {
  from { opacity: 0; transform: translateX(-32px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes flowapt-slide-down {
  from { opacity: 0; transform: translateY(-24px); }
  to { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .flowapt-animated { animation: none !important; }
}
`;

/** Full stylesheet the renderer needs: keyframes + the pseudo-class rules that
 *  can't be inlined (focus/hover), driven by CSS vars set on the panel.
 *  Rendered as an inline <style> so it works both inside the widget's shadow
 *  DOM and in the plain-DOM dev app. Safe to mount more than once. */
export const RENDERER_CSS = `
${ANIMATION_KEYFRAMES_CSS}
.fp-input-wrap:focus-within { border-color: var(--fp-focus) !important; }
input.fp-input:focus { border-color: var(--fp-focus) !important; }
/* FOCUS TREATMENT IS THE DESIGNED BORDER COLOUR ONLY (the two rules above).
   shadcn's <Input> base ships focus-visible:ring-2 + ring-offset-2 with the
   theme's default teal --ring, and Tailwind's ring-0 only zeroes the ring
   WIDTH — the emitted shadow is 0 0 0 calc(0px + var(--tw-ring-offset-width)),
   so with ring-offset-2 still in play a 2px teal ring (#3EE0CF) painted around
   every input the moment it was focused, on top of a 2px outline. A
   transition-property of 'all' then animated the whole lot over 150ms, so
   clicking a field flashed an off-brand ring in and out. Kill the ring, its
   offset and the outline on every popup input; the border colour above is the
   focus indicator. */
input.fp-input, .fp-input-wrap, .fp-input-wrap input {
  --tw-ring-shadow: 0 0 #0000 !important;
  --tw-ring-offset-shadow: 0 0 #0000 !important;
  --tw-ring-offset-width: 0px !important;
}
input.fp-input:focus, input.fp-input:focus-visible,
.fp-input-wrap input:focus, .fp-input-wrap input:focus-visible,
.fp-input-wrap:focus-within {
  box-shadow: none !important;
  outline: none !important;
}
/* Only colour animates on focus. A transition-property of 'all' also animated the
   ring/outline/box-shadow above — and any layout property that happened to
   change in the same frame — which is what made focusing a field feel janky. */
input.fp-input, .fp-input-wrap {
  transition-property: border-color, background-color, color !important;
}
input.fp-input::placeholder, .fp-input-wrap input::placeholder { color: var(--fp-placeholder); opacity: 1; }
/* While a submission is in flight the fields are locked but must not dim —
   shadcn's disabled:opacity-50 made name/email go murky while the custom
   whatsapp wrap stayed bright (patchy "half-dimmed" panel). The Processing
   button is the loading signal; the fields just hold still. */
input.fp-input:disabled, .fp-input-wrap input:disabled { opacity: 1; cursor: wait; }
.fp-btn:hover { background-color: var(--fp-btn-hover) !important; }
/* "lit" finish: lighter gradient on hover; pressed drops the top light, shades
   the inside and sinks 1px. Only an enabled button presses. */
.fp-btn.fp-btn-lit { transition: box-shadow .15s ease, transform .15s ease, background-image .15s ease !important; }
.fp-btn.fp-btn-lit:hover:not(:disabled) { background-image: var(--fp-btn-lit-hover) !important; }
.fp-btn.fp-btn-lit:active:not(:disabled) { background-image: var(--fp-btn-lit-active) !important; box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.6) !important; transform: translateY(1px) !important; }
@media (prefers-reduced-motion: reduce) {
  .fp-btn.fp-btn-lit { transition: none !important; }
  .fp-btn.fp-btn-lit:active:not(:disabled) { transform: none !important; }
}
.fp-rich a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }
.fp-rich a:hover { opacity: 0.8; }
/* Trademark symbols in display headings: many fonts (and the fallbacks that
   stand in for unloadable system faces like "Helvetica") draw ® at full cap
   height, which reads huge at heading sizes. Render as true superscript. */
.fp-tm { font-size: 0.45em; vertical-align: super; line-height: 0; }
/* Paragraphs inside one text element (<p> in the copy): no browser margins,
   a paragraph gap between them. */
.fp-rich p { margin: 0 !important; }
.fp-rich p + p { margin-top: 0.6em !important; }
/* Designer-set space above an element (SpaceAbove, spaceAboveProps). The
   repeated class out-ranks the shell's !important
   .space-y-N > :not([hidden]) ~ :not([hidden]) (0,3,0); the phone rule sits
   in the max-width block below, later in source order, so it wins there. */
.fp-mt.fp-mt.fp-mt.fp-mt { margin-top: var(--fp-mt) !important; }
/* Designer-set space between the form fields (fieldGapProps): out-ranks the
   same shell rule from the fields' container; phone rule in the block below. */
.fp-fgap.fp-fgap.fp-fgap > :not([hidden]) ~ :not([hidden]) { margin-top: var(--fp-fgap) !important; }
/* The widget shell's rem->px block forces .rounded-md to 6px with !important,
   which beat the design's INLINE input/button radius on every popup. These
   element-qualified rules out-specify it so the configured radius always
   applies (vars set on the panel). */
input.fp-input { border-radius: var(--fp-radius-input) !important; }
div.fp-input-wrap { border-radius: var(--fp-radius-input) !important; }
button.fp-btn { border-radius: var(--fp-radius-btn) !important; }
/* Same shell-block defeat for the input's horizontal padding: .px-3's
   !important beats inline paddingLeft, so pill inputs never got their extra
   room before the curve. 12px when not pill = the legacy px-3 exactly.
   The WhatsApp field's inner input keeps its own padding (icon block leads). */
input.fp-input { padding-left: var(--fp-input-px) !important; padding-right: var(--fp-input-px) !important; }
/* Card-image content paddings: explicit rule (vars set on the content div)
   because the compiled utility order makes pl/pb overrides vs px/p shorthands
   unreliable — a plain rule with !important can't be flipped by it. */
div.fp-card-content { padding: var(--fp-cc-pad-mobile) !important; }
@media (min-width: 768px) {
  div.fp-card-content { padding: var(--fp-cc-pad-md) !important; }
}
/* Spin-to-win "overhang" layout (desktop only — phones stack the wheel
   inside the panel): the wheel is centred on the panel's edge, half outside,
   and the content column pads itself past it. Vars set on the wheel row. */
@media (min-width: 768px) {
  .fp-wheel-oh { position: relative; min-height: var(--fp-wheel-size); }
  .fp-wheel-oh-stage { position: absolute; top: 50%; width: var(--fp-wheel-size) !important; height: var(--fp-wheel-size); padding: 0 !important; }
  .fp-wheel-oh-stage.fp-oh-left { left: 0; transform: translate(calc(-50% + var(--fp-wheel-oh-shift)), -50%); }
  .fp-wheel-oh-stage.fp-oh-right { right: 0; transform: translate(calc(50% - var(--fp-wheel-oh-shift)), -50%); }
  .fp-wheel-oh-stage .flowapt-wheel { width: 100% !important; }
  .fp-wheel-oh-content { width: 100% !important; }
  .fp-wheel-oh-content.fp-oh-left { padding-left: var(--fp-wheel-oh-pad) !important; }
  .fp-wheel-oh-content.fp-oh-right { padding-right: var(--fp-wheel-oh-pad) !important; }
}
/* image.hideOnMobile: phones only — no desktop rule at all, so the section's
   own layout classes (block/flex variants) stay untouched on md+. */
@media (max-width: 767px) {
  .fp-hide-mobile { display: none !important; }
  /* Phone-specific backdrop (theme.background.image.mobileUrl, or the
     background-placed layout image's mobileUrl): the desktop inline
     background-image is swapped for the portrait composition under 768px.
     Var + !important because inline styles lose to the shell's rem->px block. */
  div.fp-bg-m {
    background-image: var(--fp-bg-m) !important;
    background-size: var(--fp-bg-m-size, cover) !important;
    background-position: var(--fp-bg-m-pos, center) !important;
    background-repeat: var(--fp-bg-m-rep, no-repeat) !important;
  }
  /* Exact phone type size (TextElementStyle.mobileSizePx) / phone width —
     var + !important so a phone value can be LARGER than the desktop clamp. */
  .fp-fs-m { font-size: var(--fp-fs-m) !important; }
  .fp-w-m { width: var(--fp-w-m) !important; }
  .fp-mt-m.fp-mt-m.fp-mt-m.fp-mt-m { margin-top: var(--fp-mt-m) !important; }
  .fp-fgap-m.fp-fgap-m.fp-fgap-m > :not([hidden]) ~ :not([hidden]) { margin-top: var(--fp-fgap-m) !important; }
  /* phone full-height card: the frame follows the visible (dynamic) viewport
     so the bottom margin shows above Safari's toolbar */
  .fp-phone-card { top: 0 !important; bottom: auto !important; height: 100vh !important; height: 100dvh !important; box-sizing: border-box; }
  .flowapt-scratch button[style*="--fp-btn-h-m"] { height: var(--fp-btn-h-m) !important; }
}
/* Builder drag-resize (fp-rz-* = layout image, fp-ti-* = text image):
   explicit widths written by the on-canvas handles, applied only when the
   class is present (design field > 0) — absent class = legacy auto sizing,
   byte-identical. Vars + element-qualified !important rules, because the
   shell's rem->px block beats utilities AND inline styles. The inner div/img
   rules force the media to fill the resized column (w-fit/w-auto branches
   would otherwise shrinkwrap or overflow it). */
@media (min-width: 768px) {
  div.fp-rz-d { width: var(--fp-rz-d) !important; max-width: var(--fp-rz-d) !important; flex: 0 0 auto !important; }
  div.fp-rz-d > div { width: 100% !important; }
  div.fp-rz-d img { width: 100% !important; }
  div.fp-rz-center { margin-left: auto !important; margin-right: auto !important; }
  img.fp-ti-d { width: var(--fp-ti-d) !important; }
  /* Builder panel edge-drag live feedback (desktop only — the committed value
     lands in size.maxWidth and goes through the normal responsive clamps). */
  div.fp-pw { width: var(--fp-pw) !important; max-width: var(--fp-pw) !important; }
}
@media (max-width: 767px) {
  div.fp-rz-m { width: var(--fp-rz-m) !important; max-width: var(--fp-rz-m) !important; margin-left: auto !important; margin-right: auto !important; }
  div.fp-rz-m img { width: 100% !important; }
  img.fp-ti-m { width: var(--fp-ti-m) !important; }
}
/* Builder content-column edge-drag. NOT inside a media query, unlike the panel
   rule above: size.contentMaxWidth is a single device-independent cap that the
   renderer applies inline at every width, so the live drag must match on phone
   and desktop alike. Qualified on BOTH elements because the column renders as
   a <form> on the welcome step and a <div> on success. */
div.fp-cw, form.fp-cw { max-width: var(--fp-cw) !important; }
/* Per-device alignment: -d- classes bind ≥768px, -m- classes bind <768px
   (mobileAlign falls back to align in the renderer, so a design without a
   phone override behaves exactly as the single-align era did). */
@media (min-width: 768px) {
  img.fp-ti-d-left { margin-left: 0 !important; margin-right: auto !important; }
  img.fp-ti-d-center { margin-left: auto !important; margin-right: auto !important; }
  img.fp-ti-d-right { margin-left: auto !important; margin-right: 0 !important; }
}
@media (max-width: 767px) {
  img.fp-ti-m-left { margin-left: 0 !important; margin-right: auto !important; }
  img.fp-ti-m-center { margin-left: auto !important; margin-right: auto !important; }
  img.fp-ti-m-right { margin-left: auto !important; margin-right: 0 !important; }
}
/* Form-control drag-resize (fp-fld-*): width on the field wrapper / button,
   height vars cascade from the wrapper to the input(s) inside. All fields
   share one size (the model has ONE InputsStyle); the button has its own. */
@media (min-width: 768px) {
  div.fp-fld-w-d, button.fp-fld-w-d { width: var(--fp-fld-w-d) !important; }
  div.fp-fld-h-d input { height: var(--fp-fld-h-d) !important; }
  button.fp-fld-h-d { height: var(--fp-fld-h-d) !important; }
  button.fp-btn-fs-d { font-size: var(--fp-btn-fs-d) !important; }
}
@media (max-width: 767px) {
  div.fp-fld-w-m, button.fp-fld-w-m { width: var(--fp-fld-w-m) !important; }
  div.fp-fld-h-m input { height: var(--fp-fld-h-m) !important; }
  button.fp-fld-h-m { height: var(--fp-fld-h-m) !important; }
  button.fp-btn-fs-m { font-size: var(--fp-btn-fs-m) !important; }
}
@media (min-width: 768px) {
  div.fp-fld-d-left, button.fp-fld-d-left { margin-left: 0 !important; margin-right: auto !important; }
  div.fp-fld-d-center, button.fp-fld-d-center { margin-left: auto !important; margin-right: auto !important; }
  div.fp-fld-d-right, button.fp-fld-d-right { margin-left: auto !important; margin-right: 0 !important; }
  /* inline-flex buttons compute auto side-margins to 0 — force block-level
     flex so alignment takes; the label stays centered via justify-center */
  button.fp-fld-d-left, button.fp-fld-d-center, button.fp-fld-d-right { display: flex !important; }
}
@media (max-width: 767px) {
  div.fp-fld-m-left, button.fp-fld-m-left { margin-left: 0 !important; margin-right: auto !important; }
  div.fp-fld-m-center, button.fp-fld-m-center { margin-left: auto !important; margin-right: auto !important; }
  div.fp-fld-m-right, button.fp-fld-m-right { margin-left: auto !important; margin-right: 0 !important; }
  button.fp-fld-m-left, button.fp-fld-m-center, button.fp-fld-m-right { display: flex !important; }
  /* text elements: phone-only text-align override (desktop rides the inline
     textAlign from textCss, so it needs !important to win on phones) */
  .fp-txt-m-left { text-align: left !important; }
  .fp-txt-m-center { text-align: center !important; }
  .fp-txt-m-right { text-align: right !important; }
}
`;

// ---------- overlay ----------

export function overlayCss(theme: ResolvedStepDesign["theme"]): React.CSSProperties {
  return {
    backgroundColor: theme.overlay.color,
    ...(theme.overlay.blur > 0
      ? { backdropFilter: `blur(${theme.overlay.blur}px)`, WebkitBackdropFilter: `blur(${theme.overlay.blur}px)` }
      : {}),
  };
}
